import { useMemo, useState } from 'react';
import {
  TOP_TESTS_MODE_LABELS,
  type TimeScope,
  type TopTestsMode,
  type TopTestsResult,
  topTestsModeSchema,
} from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Segmented } from '#client/components/ui/Segmented';
import { Skeleton } from '#client/components/ui/Skeleton';
import { useApiResource } from '#client/hooks/useResourceItem';
import { useCan } from '#client/hooks/useCan';
import { topTestAside, topTestValue, topTestsPath } from './dashboard.logic';

const MODE_OPTIONS = topTestsModeSchema.options.map((mode) => ({
  value: mode,
  label: TOP_TESTS_MODE_LABELS[mode],
}));

/**
 * The five tests the lab does most of, or bills most for.
 *
 * Ranked horizontal bars, and no chart library: a top-N ranking is the one
 * chart shape that needs no SVG at all — it is a list with a width on each row.
 *
 * Its own fetch, not part of the dashboard snapshot. The mode is a toggle
 * somebody flips, and flipping it should not refetch thirteen queries plus the
 * work queue and the attention feed to reorder five rows.
 *
 * The toggle is **hidden** rather than disabled for a technician. The server
 * coerces the mode for them regardless, so a visible control would be one that
 * silently does nothing — and the reason it is withheld is that the *ordering*
 * leaks which test earns most, which a disabled control would still hint at.
 */
export function TopTestsCard({ scope }: { scope: TimeScope }) {
  const can = useCan();
  const seesBilling = can('billing.view');

  const [by, setBy] = useState<TopTestsMode>('count');
  const [paidOnly, setPaidOnly] = useState(false);

  const path = useMemo(() => topTestsPath({ scope, by, paidOnly }), [scope, by, paidOnly]);

  const { data, loading, error } = useApiResource<TopTestsResult>(path);

  return (
    <Card
      title="Top five tests"
      subtitle={data?.caption}
      actions={
        seesBilling ? (
          <Segmented
            label="Rank by"
            size="sm"
            options={MODE_OPTIONS}
            value={by}
            onChange={(mode) => setBy(mode)}
          />
        ) : undefined
      }
    >
      {loading && <TopTestsSkeleton />}

      {!loading && error && <p className="c-field__help">{error}</p>}

      {!loading && !error && data && data.rows.length === 0 && (
        <EmptyState title="Nothing in this period" body="No tests were ordered in this scope." />
      )}

      {!loading && data && data.rows.length > 0 && <TopTestsList result={data} />}

      {seesBilling && (
        <Checkbox
          label="Only visits paid in full"
          checked={paidOnly}
          onChange={(event) => setPaidOnly(event.target.checked)}
        />
      )}
    </Card>
  );
}

function TopTestsSkeleton() {
  return (
    <ul className="c-top-tests">
      {[0, 1, 2, 3, 4].map((row) => (
        <li key={row} className="c-top-test">
          <Skeleton width="40%" height="0.75rem" />
          <Skeleton height="0.5rem" />
        </li>
      ))}
    </ul>
  );
}

function TopTestsList({ result }: { result: Pick<TopTestsResult, 'by' | 'rows'> }) {
  return (
    <ol className="c-top-tests">
      {result.rows.map((row, index) => (
        <li key={row.test_id} className="c-top-test">
          <div className="c-top-test__head">
            <span className="c-top-test__rank" aria-hidden>
              {index + 1}
            </span>
            <span className="c-top-test__name" title={row.name}>
              {row.name}
            </span>
            <span className="c-top-test__value tnum">{topTestValue(row, result.by)}</span>
          </div>

          {/*
            The bar is decoration over a number that is already written out
            beside it, so it carries no accessible name of its own — a
            second announcement of the same figure is noise to a screen
            reader, not a service to it.
          */}
          <div className="c-top-test__track" aria-hidden>
            <div className="c-top-test__fill" style={{ inlineSize: `${row.share * 100}%` }} />
          </div>

          <p className="c-top-test__aside">{topTestAside(row, result.by)}</p>
        </li>
      ))}
    </ol>
  );
}
